// Un onglet n'accepte un événement d'auth que s'il correspond à SON stockage.
//
// POURQUOI
// --------
// supabase-js rejoue dans chaque onglet les événements des AUTRES onglets
// (BroadcastChannel, `GoTrueClient.ts:394-398`), avec la session de l'onglet
// ÉMETTEUR. Or une session propriétaire ou employé vit dans le
// `sessionStorage` de son seul onglet (`authStorage.ts`). Un onglet `/book`
// anonyme qui acceptait cet écho se croyait connecté comme le propriétaire,
// la porte (`sessionGuardedFetch.ts`) retenait ses requêtes, et `Book.tsx`
// renvoyait le client vers « / ». Spec du 2026-09-25 :
// `.superpowers/specs/2026-09-25-multi-onglets-echo-session-design.md`.
//
// POURQUOI LE TEST EST JUSTE POUR UN ÉVÉNEMENT LOCAL
// --------------------------------------------------
// auth-js écrit le stockage AVANT de notifier (`_saveSession`), et l'efface
// AVANT de notifier `SIGNED_OUT` (`_removeSession`). Un événement de l'onglet
// lui-même correspond donc toujours à son stockage.
//
// SENS SÛR DE L'ÉCHEC
// -------------------
// Dans le doute (natif asynchrone, contenu illisible, lecture qui lève), on
// ACCEPTE : c'est le comportement d'avant cette garde. On ne refuse que sur
// une contradiction LISIBLE.
//
// Ce module n'importe rien : testable sans navigateur ni SDK.

const EVENEMENTS_GARDES = new Set(['SIGNED_IN', 'TOKEN_REFRESHED', 'SIGNED_OUT']);

/** Rend le jeton stocké, `null` si le stockage est vide, `undefined` si on ne peut pas trancher. */
function jetonStocke(readStored: () => unknown): string | null | undefined {
  let brut: unknown;
  try {
    brut = readStored();
  } catch {
    return undefined;
  }
  if (brut === null) return null;
  // Natif : le coffre rend une Promise. Une seule vue web, aucun écho possible.
  if (typeof brut !== 'string') return undefined;
  try {
    const jeton = (JSON.parse(brut) as { access_token?: unknown } | null)?.access_token;
    return typeof jeton === 'string' && jeton !== '' ? jeton : undefined;
  } catch {
    return undefined;
  }
}

export function tabOwnsAuthEvent(
  event: string,
  session: { access_token?: string | null } | null,
  readStored: () => unknown,
): boolean {
  if (!EVENEMENTS_GARDES.has(event)) return true;

  const stocke = jetonStocke(readStored);
  if (stocke === undefined) return true;

  if (session) {
    if (!session.access_token) return true;
    // Stockage vide : la session est celle d'un autre onglet.
    return stocke === session.access_token;
  }

  // Déconnexion : acceptée seulement si l'onglet n'a plus de session à lui.
  return stocke === null;
}

/**
 * Le nom de clé sous lequel auth-js range la session (`sb-<ref>-auth-token`).
 *
 * Lu sur l'instance du SDK (`GoTrueClient.ts:297`) plutôt que recopié : une
 * copie de la règle de nommage divergerait en silence. Rend `null` si le champ
 * manque, ce qui désactive la garde (sens sûr). Un test sur un vrai
 * `createClient` casse si une montée de version le retire.
 */
export function readAuthStorageKey(auth: unknown): string | null {
  if (!auth || typeof auth !== 'object') return null;
  const cle = (auth as { storageKey?: unknown }).storageKey;
  return typeof cle === 'string' && cle !== '' ? cle : null;
}
